/**
 * Colour derivation: the arithmetic that lets the theme be built from *contrast targets*
 * rather than from hand-picked ramp stops.
 *
 * Authorised by: design-direction §2 (the legibility tiebreaker — "where the two conflict
 * resolves to Waymark, because legibility is an accessibility requirement and mood is
 * not"), §3 (temperature encoded as value and warmth; the dial scales colour saturation),
 * implementation-brief §11 (contrast is an accessibility GATE).
 *
 * Why this file exists at all: the first cut of the theme picked ramp indices by eye and
 * shipped a temperature encoding where `closed` and `composting` were the same colour in
 * dark mode (1.00:1) and every state hairline sat at 1.2–1.8:1. Hand-picking stops from a
 * 12-step ramp cannot hit a contrast target, because the ramp has no stops in the narrow
 * luminance band that fixed text colours leave available. So the theme now states the
 * target — "this surface must sit at the luminance where `textSecondary` still clears AA"
 * — and solves for the hex.
 *
 * Two properties make this safe to rely on:
 *
 *  1. `mix` blends in sRGB space. Blending any colour toward pure white or pure black
 *     scales all three channel *differences* by the same factor, so the HSL hue angle is
 *     preserved exactly. `atLuminance` therefore changes value without changing hue.
 *  2. `atLuminance` is a deterministic bisection — no randomness, no clock. The same
 *     inputs always produce the same hex, so the token tables are stable build to build.
 */

import { relativeLuminance } from './contrast';

export type Rgb = { r: number; g: number; b: number };

export function parseColour(hex: string): Rgb {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(`Not a hex colour: ${hex}`);
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Linear sRGB blend. `t = 0` returns `a`, `t = 1` returns `b`. */
export function mix(a: string, b: string, t: number): string {
  const x = parseColour(a);
  const y = parseColour(b);
  const k = Math.max(0, Math.min(1, t));
  return toHex({
    r: x.r + (y.r - x.r) * k,
    g: x.g + (y.g - x.g) * k,
    b: x.b + (y.b - x.b) * k,
  });
}

const WHITE = '#FFFFFF';
const BLACK = '#000000';

/**
 * The same hue as `anchor`, at (as close as 8-bit sRGB allows to) `target` relative
 * luminance. Lightens toward white or darkens toward black, so hue is preserved.
 *
 * `target` is clamped to [0, 1]. Bisection, 24 iterations: the residual is far below one
 * 8-bit step, so the rounding to a hex string dominates the error. Every call site adds a
 * margin to its WCAG target (4.7 for a 4.5 gate, 3.2 for a 3.0 gate) to absorb it.
 */
export function atLuminance(anchor: string, target: number): string {
  const want = Math.max(0, Math.min(1, target));
  const here = relativeLuminance(anchor);
  if (Math.abs(here - want) < 1e-6) return toHex(parseColour(anchor));
  const towards = here < want ? WHITE : BLACK;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    const l = relativeLuminance(mix(anchor, towards, mid));
    // Luminance is monotonic in t along a blend toward white (up) or black (down).
    const tooFar = here < want ? l > want : l < want;
    if (tooFar) hi = mid;
    else lo = mid;
  }
  return mix(anchor, towards, (lo + hi) / 2);
}

/**
 * The luminance a colour needs in order to sit at `ratio` against `otherLuminance`.
 * `side` says which way to go. Solved from the WCAG contrast definition, so the theme can
 * declare "3.2:1 against my own surface" and get a hex rather than a guess.
 */
export function luminanceForContrast(
  otherLuminance: number,
  ratio: number,
  side: 'lighter' | 'darker',
): number {
  const other = otherLuminance + 0.05;
  const l = side === 'lighter' ? other * ratio - 0.05 : other / ratio - 0.05;
  return Math.max(0, Math.min(1, l));
}

/** A colour at `ratio` against `against`, in the given direction, keeping `anchor`'s hue. */
export function atContrast(
  anchor: string,
  against: string,
  ratio: number,
  side: 'lighter' | 'darker',
): string {
  return atLuminance(anchor, luminanceForContrast(relativeLuminance(against), ratio, side));
}

// ---------------------------------------------------------------------------
// Hue and chroma — used by the "red is never a state" guard (design-direction §3)
// ---------------------------------------------------------------------------

/** HSL hue angle in degrees, 0 = red. Undefined for true greys, reported as 0 chroma. */
export function hueAngle(hex: string): number {
  const { r, g, b } = parseColour(hex);
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  if (d === 0) return 0;
  let h: number;
  if (max === R) h = 60 * (((G - B) / d) % 6);
  else if (max === G) h = 60 * ((B - R) / d + 2);
  else h = 60 * ((R - G) / d + 4);
  return h < 0 ? h + 360 : h;
}

/**
 * Absolute chroma, 0..1: `(max - min) / 255`.
 *
 * Deliberately NOT HSL saturation. HSL saturation of a near-black warm tint is high (a
 * 40,18,12 ember hint reads 0.5) even though the eye sees "almost black, slightly warm",
 * so an HSL-based guard would either reject the authorised ember night-ground or have to
 * be loosened until it rejected nothing. Absolute chroma tracks what is actually visible.
 */
export function chroma(hex: string): number {
  const { r, g, b } = parseColour(hex);
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
}

// ---------------------------------------------------------------------------
// Perceptual difference — the gate a materials feature cannot do without
// ---------------------------------------------------------------------------

/** CIELAB (D65), the space CIEDE2000 is defined in. */
function lab(hex: string): [number, number, number] {
  const { r, g, b } = parseColour(hex);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [R, G, B] = [lin(r), lin(g), lin(b)];
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const X = f((0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047);
  const Y = f(0.2126 * R + 0.7152 * G + 0.0722 * B);
  const Z = f((0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883);
  return [116 * Y - 16, 500 * (X - Y), 200 * (Y - Z)];
}

/**
 * CIEDE2000. Roughly 1.0 is the just-noticeable difference; under 2.0 two colours are, for
 * practical purposes, the same colour.
 *
 * WHY THE TOKEN LAYER OWES ANYONE A PERCEPTUAL METRIC. Every other colour guarantee in this
 * package is a *minimum* — a boundary must clear 3:1, text must clear 4.5:1 — and every one of
 * them is satisfied by making two things MORE different. The materials feature (docs/DEVIATIONS.md
 * M11) introduced the first guarantee that runs the other way: two materials must not be too
 * SIMILAR, or the app is charging for a difference nobody can see. Contrast ratio cannot express
 * that, because the whole safety property of a material is that its luminance is identical to
 * Drystone's — so every material pair has a contrast ratio of exactly 1.00:1 against each other,
 * and passes every existing gate while being invisible. Hue and chroma are the only budget a
 * material has, and this is the function that says whether it spent them.
 */
export function deltaE2000(a: string, b: string): number {
  const [L1, a1, b1] = lab(a);
  const [L2, a2, b2] = lab(b);
  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cb = (C1 + C2) / 2;
  const G = 0.5 * (1 - Math.sqrt(Cb ** 7 / (Cb ** 7 + 25 ** 7)));
  const A1 = a1 * (1 + G);
  const A2 = a2 * (1 + G);
  const Cp1 = Math.hypot(A1, b1);
  const Cp2 = Math.hypot(A2, b2);
  const h1 = ((Math.atan2(b1, A1) * 180) / Math.PI + 360) % 360;
  const h2 = ((Math.atan2(b2, A2) * 180) / Math.PI + 360) % 360;
  const dLp = L2 - L1;
  const dCp = Cp2 - Cp1;
  let dhp = 0;
  if (Cp1 * Cp2 !== 0) {
    dhp = h2 - h1;
    if (dhp > 180) dhp -= 360;
    if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(Cp1 * Cp2) * Math.sin((dhp * Math.PI) / 360);
  const Lbp = (L1 + L2) / 2;
  const Cbp = (Cp1 + Cp2) / 2;
  let hbp = h1 + h2;
  if (Cp1 * Cp2 !== 0 && Math.abs(h1 - h2) > 180) hbp += hbp < 360 ? 360 : -360;
  hbp /= 2;
  const T =
    1 -
    0.17 * Math.cos(((hbp - 30) * Math.PI) / 180) +
    0.24 * Math.cos((2 * hbp * Math.PI) / 180) +
    0.32 * Math.cos(((3 * hbp + 6) * Math.PI) / 180) -
    0.2 * Math.cos(((4 * hbp - 63) * Math.PI) / 180);
  const Sl = 1 + (0.015 * (Lbp - 50) ** 2) / Math.sqrt(20 + (Lbp - 50) ** 2);
  const Sc = 1 + 0.045 * Cbp;
  const Sh = 1 + 0.015 * Cbp * T;
  const Rt =
    -2 *
    Math.sqrt(Cbp ** 7 / (Cbp ** 7 + 25 ** 7)) *
    Math.sin((60 * Math.exp(-(((hbp - 275) / 25) ** 2)) * Math.PI) / 180);
  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

/** Smallest angular distance between two hues, in degrees. */
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * LAW (design-direction §3, CLAUDE.md): **red is never a state.**
 *
 * A colour counts as red when it is within 20° of hue 0 AND carries enough chroma to read
 * as a hue rather than as a warm neutral. `ember` — which design-direction §2 authorises
 * as the warmth of the lowest-stimulation night theme — is an orange-red ramp, so this
 * predicate is what keeps its authorised use (a near-neutral hint on the deep night
 * ground) separate from its forbidden use (a saturated alarm fill).
 */
export const RED_HUE_TOLERANCE = 20;
export const RED_CHROMA_FLOOR = 0.12;

export function isRed(hex: string): boolean {
  return (
    hueDistance(hueAngle(hex), 0) <= RED_HUE_TOLERANCE && chroma(hex) >= RED_CHROMA_FLOOR
  );
}

/**
 * THE ONE EXEMPTION FROM THE LAW ABOVE, AND THE ONLY ONE THERE WILL BE WITHOUT ANOTHER RULING.
 *
 * `docs/RULINGS.md` R196 (founder decision; `docs/DEVIATIONS.md` `FD8`) authorises a Pride
 * material whose `warm` role is the flag's red. Nothing else in the table may be red, and
 * loosening `RED_HUE_TOLERANCE` or `RED_CHROMA_FLOOR` to accommodate it would have done exactly
 * that — a threshold cannot be widened for one material. So the exemption is a NAMED LIST, and
 * `test/materials.test.ts` still scans every token of every material not on it at full strength.
 *
 * WHAT THE EXEMPTION DOES NOT DO. It does not make red a state anywhere. `deriveTemperatures()`
 * in `@carn/core` is still the only thing that assigns a temperature; the colour a temperature is
 * drawn in is identity, and every state stays redundantly carried by position, icon and label
 * (design-direction §3). There is still no `danger` ramp, no red badge and no overdue state — the
 * gate over the other six materials is what keeps it that way, and this list does not reach them.
 *
 * ADDING AN ID HERE IS A LAW CHANGE, not a fix for a failing test. If a material you are writing
 * trips the gate, the ramp is wrong.
 */
export const RED_GATE_EXEMPT: readonly string[] = ['pride'];
